import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react'

/**
 * Whether the screen inside the shell is holding work nobody has saved - `SAAS.md` §3.175, FB-941.
 *
 * **Why this exists at all.** All three AGOS apps are on `wouter`, which exposes `Link`, `Route` and
 * `useLocation` and **no blocker**: `useBlocker` appears nought times in the whole frontend because there is
 * nothing to import. So `beforeunload` is the only mechanism a dirty page has, and it covers a closed tab and
 * a refresh while seeing nothing at all of the commonest way anybody leaves a screen - a click on the
 * sidebar. Measured: four files across the product arm `beforeunload` and **none** of them guards a route
 * change.
 *
 * **A ref rather than state, and that is deliberate.** The shell renders the page, so holding this in state
 * would re-render the whole shell on every keystroke in an editor it contains. The shell does not need to
 * know *while* somebody types - it needs the answer **at the moment of the click**, which is exactly what a
 * ref gives and what `useUnsavedWorkGate` reads.
 *
 * **It fails open.** Outside a provider the gate answers false, so nothing is blocked. That is
 * `franchise-modules-context`'s own reasoning pointed at navigation: a guard that cannot read the state it
 * guards must not be the thing standing between somebody and the rest of the application.
 */
interface UnsavedWorkContextValue {
  /** Read at the click rather than subscribed to - see the note above on why this is not state. */
  held: { current: boolean }
  set: (held: boolean) => void
}

const UnsavedWorkContext = createContext<UnsavedWorkContextValue | null>(null)

export function UnsavedWorkProvider({ children }: { children: ReactNode }) {
  const held = useRef(false)
  const value = useMemo<UnsavedWorkContextValue>(
    () => ({
      held,
      set: (next: boolean) => {
        held.current = next
      },
    }),
    [],
  )
  return <UnsavedWorkContext.Provider value={value}>{children}</UnsavedWorkContext.Provider>
}

/**
 * A page declares that it is holding unsaved work.
 *
 * **Cleared on unmount, which is the half a bare boolean gets wrong.** The cleanup sets it false, so a page
 * that navigates away while dirty cannot leave the flag standing for whatever renders next - which is the
 * shape `edited !== null` had one layer up in §3.174, where `saved()` cleared the sections and left the live
 * draft alone, so the warning would have outlived the thing it was about.
 */
export function useUnsavedWork(held: boolean): void {
  const context = useContext(UnsavedWorkContext)
  useEffect(() => {
    if (context === null) return
    context.set(held)
    return () => context.set(false)
  }, [context, held])
}

/**
 * The shell asks, at the moment somebody tries to leave.
 *
 * Returns a function rather than a boolean **because it must not be read at render time**: a shell that
 * re-rendered on the answer would be back to holding it in state.
 */
export function useUnsavedWorkGate(): () => boolean {
  const context = useContext(UnsavedWorkContext)
  return () => context?.held.current === true
}
